import React, { useEffect, useState } from "react";
import {
  List,
  ListItem,
  CircularProgress,
  Box,
  Typography,
} from "@mui/material";
import { useApi } from "../../../api/ApiProvider";
import { CreateTreatmentNoteDto, TimeSlotDto } from "../../../api-client";
import TreatmentNoteCard from "./TreatmentNoteCard";
import notificationStore from "../../../stores/Notification/notificationStore";

interface TreatmentNoteListProps {
  patientId: number;
  limit: number;
  infiniteLoading?: boolean;
}

export default function TreatmentNoteList({ patientId, limit }: TreatmentNoteListProps) {
  const { clinicalDocumentation } = useApi();

  const [treatmentNotes, setTreatmentNotes] = useState<TimeSlotDto[]>([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  useEffect(() => {
    if (patientId) {
      generateItems();
    }
  }, [patientId]);

  const generateItems = async () => {
    try {
      setLoading(true);
      const timeSlots = await clinicalDocumentation.getClinicalTreatmentNotesWithFilters(patientId, limit, page);
      if (timeSlots.length) {
        setTreatmentNotes((prev) => [...prev, ...timeSlots]);
        setPage(page + 1);
      } else {
        setHasMore(false);
      }
    } catch (error: any) {
      console.error("Error fetching treatment notes:", error);
      notificationStore.showMessage(error?.message, 'error', error);
    } finally {
      setLoading(false);
    }
  };

  const updateTreatmentNote = async (id: number, dto: CreateTreatmentNoteDto) => {
    try {
      const data = await clinicalDocumentation.updateClinicalTreatmentNote(id, dto);
      return data;
    } catch (error: any) {
      console.error(error);
      throw error;
    }
  };

  const createTreatmentNote = async (dto: CreateTreatmentNoteDto) => {
    try {
      const data = await clinicalDocumentation.createClinicalTreatmentNote(dto);
      return data;
    } catch (error: any) {
      console.error(error);
      notificationStore.showMessage(error?.message, 'error', error);
      throw error;
    }
  };

  return (
    <Box sx={{width: "100%", padding: 1}}>
      <List sx={{ width: "100%" }} >
        {treatmentNotes.map((timeSlot) => (
          <ListItem key={timeSlot.id} disableGutters>
            <TreatmentNoteCard
              timeSlot={timeSlot}
              onUpdateTreatmentNote={updateTreatmentNote}
              onCreateTreatmentNote={createTreatmentNote}
              patientId={patientId}
            />
          </ListItem>
        ))}
      </List>

      {loading && (
        <Box display="flex" justifyContent="center" py={2}>
          <CircularProgress />
        </Box>
      )}

      {hasMore && !loading && (
        <Box display="flex" justifyContent="center" py={2}>
          <Typography
            variant="body2"
            onClick={generateItems}
            style={{
              cursor: "pointer",
              color: "#3f51b5",
              textDecoration: "underline",
            }}
          >
            Load more
          </Typography>
        </Box>
      )}

      {!hasMore && treatmentNotes.length > 0 && (
        <Box display="flex" justifyContent="center" py={2}>
          <Typography variant="body2" color="textSecondary">
            Keine weiteren Behandlungsnotizen vorhanden.
          </Typography>
        </Box>
      )}
    </Box>
  );
}
